import type { Invoice } from "../types";
import {
  invoiceCanDownloadPdf,
  invoiceShowsIssuedSection,
  invoiceShowsPendingBanner,
} from "../invoiceDisplay";

type Props = {
  invoice: Invoice;
  loading?: boolean;
  onClose: () => void;
};

function shortIsoDate(iso: string): string {
  const s = (iso || "").trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : s.slice(0, 10);
}

function buyerTitle(inv: Invoice): string {
  const name = (inv.buyerName || "").trim() || "用户";
  const t = (inv.buyerTypeText || "").trim();
  return t ? `${name}（${t}）` : name;
}

export default function InvoiceDetailModal({
  invoice,
  loading = false,
  onClose,
}: Props) {
  const showIssued = invoiceShowsIssuedSection(invoice);
  const showPending = invoiceShowsPendingBanner(invoice);
  const canPdf = invoiceCanDownloadPdf(invoice);

  const metaRows = [
    { label: "发票代码", value: invoice.invoiceCode },
    { label: "发票号码", value: invoice.invoiceNumber },
    { label: "校验码", value: invoice.checkCode },
    {
      label: "开票日期",
      value: (invoice.updateTime || "").trim() || invoice.date,
    },
  ].filter((row) => row.label === "开票日期" || (row.value || "").trim());

  const showSeller =
    (invoice.seller || "").trim() || (invoice.sellerTax || "").trim();

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{
        background: "#F7F7F7",
        fontFamily: "'SF Pro Text','PingFang SC',sans-serif",
      }}
    >
      <div
        className="relative overflow-hidden flex-shrink-0"
        style={{
          background:
            "linear-gradient(145deg,#78350F 0%,#92400E 60%,#B45309 100%)",
        }}
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute rounded-full"
            style={{
              width: 160,
              height: 160,
              top: -30,
              right: -10,
              background: "rgba(255,255,255,0.06)",
              filter: "blur(30px)",
            }}
          />
        </div>
        <div className="relative px-5 pb-5" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
          <div className="flex items-center gap-3 mb-4">
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer flex-shrink-0"
              style={{
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              <i
                className="ri-arrow-left-line"
                style={{ fontSize: 18, color: "#fff" }}
              />
            </button>
            <div className="flex items-center gap-2 flex-1">
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: "rgba(255,255,255,0.15)" }}
              >
                <i className="ri-receipt-line text-white" style={{ fontSize: 14 }} />
              </div>
              <div>
                <div className="text-sm font-bold text-white leading-tight">
                  电子发票
                </div>
                <div
                  className="text-xs leading-tight"
                  style={{ color: "rgba(255,255,255,0.5)" }}
                >
                  {invoice.id}
                </div>
              </div>
            </div>
            <span
              className="text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap"
              style={{
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              {invoice.status}
            </span>
          </div>
          <div className="text-lg font-bold text-white mb-1">{invoice.title}</div>
          <div className="text-sm" style={{ color: "rgba(255,255,255,0.6)" }}>
            {invoice.type} · {invoice.date}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 relative">
        {loading ? (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2"
            style={{ background: "rgba(247,247,247,0.88)" }}
          >
            <i
              className="ri-loader-4-line animate-spin"
              style={{ fontSize: 32, color: "#D97706" }}
            />
            <span className="text-xs text-gray-400">加载详情...</span>
          </div>
        ) : null}

        {!showIssued ? (
          <div className="rounded-2xl p-4" style={{ background: "#fff" }}>
            <div className="text-center">
              <div className="text-xs text-gray-400 mb-0.5">申请开票金额（含税）</div>
              <div className="text-2xl font-bold" style={{ color: "#EF4444" }}>
                ¥
                {invoice.amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          </div>
        ) : null}

        {showIssued ? (
          <div className="rounded-2xl p-4" style={{ background: "#fff" }}>
            <div className="text-center mb-4">
              <div className="text-xs text-gray-400 mb-0.5">发票金额（含税）</div>
              <div className="text-3xl font-bold" style={{ color: "#EF4444" }}>
                ¥
                {invoice.amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
              <div className="text-xs text-gray-400 mt-1">税率：以实际开具为准</div>
            </div>
            {metaRows.length > 0 ? (
              <div className="space-y-2">
                {metaRows.map(({ label, value }) => (
                  <div
                    key={label}
                    className="flex justify-between py-1.5"
                    style={{ borderBottom: "1px solid #F8F8F8" }}
                  >
                    <span className="text-xs text-gray-400">{label}</span>
                    <span className="text-sm font-medium text-gray-700 font-mono text-right max-w-[65%] break-all">
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {invoice.orderInfo &&
        (invoice.orderInfo.order_no || invoice.orderInfo.hotel_name) ? (
          <div className="rounded-2xl p-4" style={{ background: "#fff" }}>
            <div className="text-sm font-bold text-gray-900 mb-3">关联订单</div>
            <div className="space-y-2">
              {[
                ["订单号", invoice.orderInfo.order_no],
                ["酒店", invoice.orderInfo.hotel_name],
                ["房型", invoice.orderInfo.room_name],
                [
                  "入住 / 离店",
                  [shortIsoDate(invoice.orderInfo.check_in_date), shortIsoDate(invoice.orderInfo.check_out_date)]
                    .filter(Boolean)
                    .join(" · "),
                ],
                [
                  "订单金额",
                  invoice.orderInfo.total_amount
                    ? `¥${Number(invoice.orderInfo.total_amount).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`
                    : "",
                ],
              ]
                .filter(([, v]) => (v || "").toString().trim())
                .map(([label, value]) => (
                  <div
                    key={String(label)}
                    className="flex justify-between py-1.5 gap-2"
                    style={{ borderBottom: "1px solid #F8F8F8" }}
                  >
                    <span className="text-xs text-gray-400 flex-shrink-0">{label}</span>
                    <span className="text-xs font-medium text-gray-800 text-right break-all">
                      {value}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        ) : null}

        <div className="rounded-2xl p-4" style={{ background: "#fff" }}>
          <div className="text-sm font-bold text-gray-900 mb-3">开票信息</div>
          <div className="space-y-3">
            <div className="p-3 rounded-xl" style={{ background: "#F8F9FC" }}>
              <div className="text-xs font-semibold text-gray-500 mb-2">申请人</div>
              <div className="text-sm text-gray-800 font-medium">{buyerTitle(invoice)}</div>
              {invoice.userPhone ? (
                <div className="text-xs text-gray-400 mt-0.5">
                  手机：{invoice.userPhone}
                </div>
              ) : null}
              <div className="text-xs text-gray-400 mt-0.5">邮箱：{invoice.email}</div>
              {(invoice.invoiceHeadName || "").trim() ? (
                <div className="text-xs text-gray-600 mt-2 pt-2 border-t border-gray-100">
                  <span className="text-gray-400">发票抬头：</span>
                  {invoice.invoiceHeadName}
                </div>
              ) : null}
              {(invoice.buyerDutyNumber || "").trim() ? (
                <div className="text-xs text-gray-600 mt-1">
                  <span className="text-gray-400">税号：</span>
                  {invoice.buyerDutyNumber}
                </div>
              ) : null}
            </div>
            {showSeller ? (
              <div className="p-3 rounded-xl" style={{ background: "#F8F9FC" }}>
                <div className="text-xs font-semibold text-gray-500 mb-2">
                  销售方（卖方）
                </div>
                <div className="text-sm text-gray-800 font-medium">
                  {(invoice.seller || "").trim() || "—"}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  税号：{(invoice.sellerTax || "").trim() || "—"}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="rounded-2xl p-4" style={{ background: "#fff" }}>
          <div className="text-sm font-bold text-gray-900 mb-3">发票明细</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ borderBottom: "1px solid #F0F0F0" }}>
                  <th className="text-left text-gray-400 pb-2 font-medium">项目</th>
                  <th className="text-center text-gray-400 pb-2 font-medium">数量</th>
                  <th className="text-right text-gray-400 pb-2 font-medium">金额</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, i) => (
                  <tr
                    key={`${item.name}-${i}`}
                    style={{
                      borderBottom:
                        i < invoice.items.length - 1 ? "1px solid #F8F8F8" : "none",
                    }}
                  >
                    <td className="py-2.5 text-gray-700 font-medium">
                      <div>{item.name}</div>
                      <div className="text-gray-400 font-normal mt-0.5">{item.spec}</div>
                    </td>
                    <td className="py-2.5 text-center text-gray-600">
                      {item.qty}
                      {item.unit}
                    </td>
                    <td className="py-2.5 text-right font-semibold text-gray-800">
                      ¥
                      {item.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: "1.5px solid #F0F0F0" }}>
                  <td colSpan={2} className="pt-3 text-sm font-bold text-gray-900">
                    合计
                  </td>
                  <td
                    className="pt-3 text-right text-sm font-bold"
                    style={{ color: "#EF4444" }}
                  >
                    ¥
                    {invoice.amount.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {showPending ? (
          <div
            className="rounded-2xl p-4"
            style={{ background: "#FEF3C7", border: "1px solid #FDE68A" }}
          >
            <div className="flex items-start gap-2">
              <i
                className="ri-time-line flex-shrink-0 mt-0.5"
                style={{ color: "#D97706", fontSize: 15 }}
              />
              <div className="text-xs text-amber-700 leading-relaxed">
                您的发票申请正在处理中，预计1-3个工作日内完成开具，开具后将发送至您的邮箱。
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {showIssued ? (
        <div
          className="flex-shrink-0 bg-white px-5 py-4 flex gap-3"
          style={{ borderTop: "1px solid #F5F5F5" }}
        >
          {/* <button
            type="button"
            className="flex-1 py-3 rounded-2xl text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            style={{ background: "#F5F5F5", color: "#6B7280" }}
          >
            <i className="ri-mail-send-line" style={{ fontSize: 15 }} />
            发送邮箱
          </button> */}
          {canPdf && invoice.invoiceUrl ? (
            <button
              type="button"
              onClick={() =>
                window.open(invoice.invoiceUrl, "_blank", "noopener,noreferrer")
              }
              className="flex-1 py-3 rounded-2xl text-sm font-semibold text-white cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
              style={{ background: "linear-gradient(135deg,#D97706,#F59E0B)" }}
            >
              <i className="ri-download-2-line" style={{ fontSize: 15 }} />
              下载
            </button>
          ) : (
            <div
              className="flex-1 py-3 rounded-2xl text-xs font-medium flex items-center justify-center text-center px-2"
              style={{ background: "#F9FAFB", color: "#9CA3AF" }}
            >
              电子票出具后可下载 
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
